import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AptiveIcon } from '@aptive-env/storybook'
import ImageCheckboxChecked from 'assets/image/checkbox-checked.png'
import ImageCheckboxUnchecked from 'assets/image/checkbox-unchecked.png'

export interface MultiSelectOption {
  value: number
  label: string
  image?: ReactNode
}

export interface MultiSelectProps {
  options: MultiSelectOption[]
  value: MultiSelectOption[]
  icon?: string
  placeholder?: string
  maxVisibleOptions?: number
  onChange: (value: MultiSelectOption[]) => void
  onVisibleOptions?: (options: MultiSelectOption[]) => void
  renderImage?: (option: MultiSelectOption) => ReactNode
}

const MultiSelect: FC<MultiSelectProps> = ({
  icon,
  placeholder,
  maxVisibleOptions,
  options,
  value,
  onChange,
  onVisibleOptions,
  renderImage,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [searchText, setSearchText] = useState('')

  const isAllOptionsSelected = useMemo(() => {
    return options.length === value.length
  }, [options, value])

  const filteredOptions = useMemo(() => {
    return options
      .filter((item) => item.label.toLowerCase().includes(searchText.toLowerCase()))
  }, [options, searchText])

  const isOptionSelected = useCallback((option: MultiSelectOption) => {
    return value.some((item) => item.value === option.value)
  }, [value])

  const handleClickOption = (option: MultiSelectOption) => {
    if (isOptionSelected(option)) {
      onChange(value.filter((item) => item.value !== option.value))
    } else {
      onChange([...value, option])
    }
  }

  const handleClickSelectAll = () => {
    if (!isAllOptionsSelected) {
      onChange(options)
    } else {
      onChange([])
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      if (!onVisibleOptions) {
        return
      }

      const container = ref.current
      if (!container) {
        return
      }

      const containerRect = container.getBoundingClientRect()
      const allItems = container.querySelectorAll('.item')
      const visibleOptions: MultiSelectOption[] = []

      allItems.forEach((item) => {
        const itemRect = item.getBoundingClientRect()
        // Check if the item is within the container's visible area
        if (
          item instanceof HTMLElement &&
          itemRect.top >= containerRect.top &&
          itemRect.bottom <= containerRect.bottom
        ) {
          const value = item.dataset.value
          const label = item.dataset.label
          const image = item.dataset.image
          visibleOptions.push({
            value: Number(value),
            label: label || '',
            image
          })
        }
      })

      onVisibleOptions(visibleOptions)
    }

    document.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [onVisibleOptions])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (event.target instanceof HTMLElement && ref.current && !ref.current.contains(event.target)) {
        event.stopPropagation()
        setIsExpanded(false)
      }
    }

    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [])

  return (
    <div className="w-full relative">
      <div
        className="flex items-center h-[38px] w-full border border-ui-gray-200 rounded cursor-pointer px-2 rounded-lg"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        {icon && (
          <AptiveIcon className="w-[24px] h-[24px] stroke-[#9CA3AF] fill-none" icon={icon} />
        )}
        {placeholder && (
          <div className="ml-2 flex flex-col grow">
            <input
              type="text"
              placeholder={placeholder}
              className="text-[#808080] text-sm leading-4 font-normal w-full outline-none"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
        )}
      </div>

      {isExpanded && (
        <div
          ref={ref}
          className="rounded-lg overflow-hidden absolute top-11 z-[100] bg-white w-full border border-ui-gray-200"
        >
          <div
            className="overflow-auto"
            style={{ maxHeight: maxVisibleOptions ? `${32 + (maxVisibleOptions * 52)}px` : 'auto' }}
          >
            <div
              className="item flex items-center justify-between h-[32px] px-3 cursor-pointer hover:bg-ui-gray-100"
              onClick={handleClickSelectAll}
            >
              <div className="text-ui-gray-600 text-[14px]">Select all</div>
              <div>
                {isAllOptionsSelected ? (
                  <img src={ImageCheckboxChecked} alt="ImageCheckboxChecked" />
                ) : (
                  <img src={ImageCheckboxUnchecked} alt="ImageCheckboxUnchecked" />
                )}
              </div>
            </div>
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="item flex items-center justify-between h-[52px] border-t border-t-ui-gray-200 px-3 cursor-pointer hover:bg-ui-gray-100"
                data-value={option.value}
                data-label={option.label}
                data-image={option.image}
                onClick={() => handleClickOption(option)}
              >
                <div className="flex items-center gap-3">
                  {renderImage
                    ? renderImage(option)
                    : option.image
                      ? option.image
                      : null
                  }
                  <div className="text-ui-gray-600 text-[14px]">{option.label}</div>
                </div>
                {isOptionSelected(option) ? (
                  <img src={ImageCheckboxChecked} alt="ImageCheckboxChecked" />
                ) : (
                  <img src={ImageCheckboxUnchecked} alt="ImageCheckboxUnchecked" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default MultiSelect
